import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import { EgdsSizingLengths } from '../types/index.esm.js';
import { MakeEgdsSizingLength } from '../../utils/egds-sizing-length/index.esm.js';

/**
 * Check string is a valid EgdsSizingLength value
 *
 * @param size {string} - size
 * @returns EgdsSizingLengths.includes(size as EgdsSizingLength)
 */
const IsEgdsSize = size => EgdsSizingLengths.includes(size);
/**
 * Check string is a valid FlexibleEgdsSizingLength value
 *
 * @param length {string} - length
 * @returns FlexibleEgdsSizingLength.includes(size as EgdsSizingLength)
 */
const IsFlexibleEgdsSize = length => length.endsWith('px');
/**
 * Check for deprecated "auto_fit" or "auto_fill" keys
 *
 * @param obj {UitkLayoutConditionalGridTrack} - object
 * @returns boolean
 */
const isAutoRepeatGrid = obj => Object.prototype.hasOwnProperty.call(obj, 'auto_fill') || Object.prototype.hasOwnProperty.call(obj, 'auto_fit');
/**
 * Check for "small" key
 *
 * This is used when checking values that can be applied per breakpoint, as "small" queries are
 * treated as regular values (ie: not encased in a media query when used in the CSS)
 *
 * @param key {string} - key
 * @returns boolean
 */
const isSmallKey = key => key === 'small';
/**
 * Outputs a css custom property for a valid EgdsSizingLength value
 *
 * @param value {EgdsSizingLength} - value
 * @returns `var(--uitk-layoutgrid-egds-size__${value})`
 */
const EgdsSizeCustomProp = value => `var(--uitk-layoutgrid-egds-size__${value})`;
/**
 * Returns a track item, checking if it is an EGDS size that needs to be processed
 *
 * @param item {any} - track item to return
 */
const MaketrackItem = item => {
  let trackItem;
  if (IsEgdsSize(item)) {
    trackItem = EgdsSizeCustomProp(item);
  } else if (IsFlexibleEgdsSize(item)) {
    trackItem = MakeEgdsSizingLength(item);
  } else {
    trackItem = item;
  }
  return trackItem;
};
/**
 * Outputs a css fit-content() function
 *
 * @param length {EgdsSizingLength | FlexibleEgdsSizingLength} - length
 * @returns {`fit-content(${typeof fitContentValue})`} `fit-content(length)`
 */
const FitContent = length => {
  const fitContentValue = MaketrackItem(length);
  const fitcontentValue = `fit-content(${fitContentValue})`;
  return fitcontentValue;
};
/**
 * Outputs a css minmax() function
 *
 * @param min {UitkLayoutGridLength} - min value
 * @param max {UitkLayoutGridLength} - max value
 * @returns {`minmax(${typeof minValue}, ${typeof maxValue})`} `minmax(min, max)`
 */
const MinMax = (min, max) => {
  const minValue = MaketrackItem(min);
  const maxValue = MaketrackItem(max);
  const minmaxValue = `minmax(${minValue}, ${maxValue})`;
  return minmaxValue;
};
/**
 * Outputs a css repeat() function
 *
 * @param repeatType {number | 'auto-fit' | 'auto-fill'} - repeat type
 * @param track {UitkLayoutGridLength | ReturnType<typeof MinMax> | ReturnType<typeof FitContent>} - track to repeat
 * @returns {`repeat(${typeof repeatType}, ${typeof track})`} `repeat(repeatType, track)`
 */
const Repeat = (repeatType, track) => {
  const trackValue = MaketrackItem(track);
  const repeatValue = `repeat(${repeatType}, ${trackValue})`;
  return repeatValue;
};
export { EgdsSizeCustomProp, FitContent, IsEgdsSize, IsFlexibleEgdsSize, MaketrackItem, MinMax, Repeat, isAutoRepeatGrid, isSmallKey };